import {
  compareHuge,
  huge,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";
import {
  computeMpmAssignedMpOccupancy,
  computeMpmAvailableMpFromOccupancy,
} from "../mpm/mpmCapacity";
import { normalizeMpmState, type MpmState } from "../mpm/mpmState";
import { cloneExplorationState } from "./explorationState";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationBiomeId,
  type ExplorationCommandBlockedReason,
  type ExplorationDefinition,
  type ExplorationMpSlotDefinition,
  type ExplorationMpSlotId,
  type ExplorationMpSummary,
  type ExplorationState,
} from "./explorationTypes";

export type ExplorationPurchaseCommandResult =
  | {
      readonly ok: true;
      readonly state: ExplorationState;
      readonly cost: HugeNumber;
      readonly purchasedSlot: ExplorationMpSlotDefinition;
    }
  | {
      readonly ok: false;
      readonly reason: ExplorationCommandBlockedReason;
      readonly state: ExplorationState;
    };

export type ExplorationAssignmentCommandResult =
  | { readonly ok: true; readonly mpmState: MpmState }
  | {
      readonly ok: false;
      readonly reason: ExplorationCommandBlockedReason;
      readonly mpmState: MpmState;
    };

export function resolvePurchaseExplorationMpSlot(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly availableHovCore: HugeNumberInput;
}): ExplorationPurchaseCommandResult {
  const nextSlot = input.definition.mpSlots[input.state.purchasedMpSlotCount];
  if (nextSlot === undefined) {
    return purchaseFailure(input.state, "all-mp-slots-unlocked");
  }
  const cost =
    input.definition.mpSlotCosts[input.state.purchasedMpSlotCount] ?? null;
  if (cost === null) {
    return purchaseFailure(input.state, "price-unconfigured");
  }
  if (compareHuge(huge(input.availableHovCore), cost) < 0) {
    return purchaseFailure(input.state, "insufficient-hov-core");
  }
  return {
    ok: true,
    cost,
    purchasedSlot: nextSlot,
    state: {
      ...cloneExplorationState(input.state),
      purchasedMpSlotCount: input.state.purchasedMpSlotCount + 1,
    },
  };
}

export function resolveAssignExplorationMpSlot(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
  readonly totalMpCapacity: number;
  readonly slotId: ExplorationMpSlotId;
}): ExplorationAssignmentCommandResult {
  const mpmState = normalizeMpmState(input.mpmState);
  const slotIndex = input.definition.mpSlots.findIndex(
    (slot) => slot.id === input.slotId,
  );
  if (slotIndex < 0) {
    return assignmentFailure(mpmState, "invalid-mp-slot");
  }
  if (slotIndex >= input.state.purchasedMpSlotCount) {
    return assignmentFailure(mpmState, "mp-slot-locked");
  }
  if (mpmState.exploration.assignedSlotIds.includes(input.slotId)) {
    return assignmentFailure(mpmState, "already-assigned");
  }
  if (!mpmState.exploration.enabled) {
    return assignmentFailure(mpmState, "mpm-section-disabled");
  }
  const slot = input.definition.mpSlots[slotIndex]!;
  if (
    !Number.isSafeInteger(input.totalMpCapacity) ||
    input.totalMpCapacity < 0 ||
    computeMpmAssignedMpOccupancy(mpmState) + slot.amount >
      input.totalMpCapacity
  ) {
    return assignmentFailure(mpmState, "global-mp-capacity-exceeded");
  }
  return {
    ok: true,
    mpmState: normalizeMpmState({
      ...mpmState,
      exploration: {
        ...mpmState.exploration,
        assignedSlotIds: [
          ...mpmState.exploration.assignedSlotIds,
          input.slotId,
        ],
      },
    }),
  };
}

export function resolveRemoveExplorationMpSlot(input: {
  readonly mpmState: MpmState;
  readonly slotId: ExplorationMpSlotId;
}): ExplorationAssignmentCommandResult {
  const mpmState = normalizeMpmState(input.mpmState);
  if (!mpmState.exploration.assignedSlotIds.includes(input.slotId)) {
    return assignmentFailure(mpmState, "not-assigned");
  }
  return {
    ok: true,
    mpmState: normalizeMpmState({
      ...mpmState,
      exploration: {
        ...mpmState.exploration,
        assignedSlotIds: mpmState.exploration.assignedSlotIds.filter(
          (slotId) => slotId !== input.slotId,
        ),
      },
    }),
  };
}

export function reconcileExplorationAssignmentsToPurchasedSlots(input: {
  readonly state: ExplorationState;
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
}): MpmState {
  const purchasedIds = new Set(
    input.definition.mpSlots
      .slice(0, input.state.purchasedMpSlotCount)
      .map((slot) => slot.id),
  );
  const normalized = normalizeMpmState(input.mpmState);
  return normalizeMpmState({
    ...normalized,
    exploration: {
      ...normalized.exploration,
      assignedSlotIds: normalized.exploration.assignedSlotIds.filter((slotId) =>
        purchasedIds.has(slotId),
      ),
    },
  });
}

export function getExplorationAssignedMpByBiome(input: {
  readonly definition: ExplorationDefinition;
  readonly mpmState: MpmState;
}): Readonly<Record<ExplorationBiomeId, number>> {
  const assignedIds = new Set(input.mpmState.exploration.assignedSlotIds);
  const result = Object.fromEntries(
    EXPLORATION_BIOME_IDS.map((biomeId) => [biomeId, 0]),
  ) as Record<ExplorationBiomeId, number>;
  if (!input.mpmState.exploration.enabled) {
    return result;
  }
  for (const slot of input.definition.mpSlots) {
    if (assignedIds.has(slot.id)) {
      result[slot.biomeId] += slot.amount;
    }
  }
  return result;
}

export function getExplorationMpSummary(input: {
  readonly mpmState: MpmState;
  readonly totalMpCapacity: number;
}): ExplorationMpSummary {
  const total =
    Number.isSafeInteger(input.totalMpCapacity) && input.totalMpCapacity >= 0
      ? input.totalMpCapacity
      : 0;
  const used = computeMpmAssignedMpOccupancy(input.mpmState);
  return {
    used,
    total,
    available: computeMpmAvailableMpFromOccupancy({
      mpCapacity: total,
      state: input.mpmState,
    }),
  };
}

function purchaseFailure(
  state: ExplorationState,
  reason: ExplorationCommandBlockedReason,
): ExplorationPurchaseCommandResult {
  return { ok: false, reason, state };
}

function assignmentFailure(
  mpmState: MpmState,
  reason: ExplorationCommandBlockedReason,
): ExplorationAssignmentCommandResult {
  return { ok: false, reason, mpmState };
}
